import React, { useState, useEffect } from "react";
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { getTextColor } from "../../components/globalFunctions/globalFunctions";
import { Accordion, AccordionTab } from "primereact/accordion";
import Task from "./task";
import { Calendar } from "primereact/calendar";
import { ADD_GROUP_URL, CREATE_TASK_URL, DELETE_TASK_URL } from "../../global/API";
import Cookies from "js-cookie";
import Swal from "sweetalert2";
import { ColorPicker } from "primereact/colorpicker";

const DEFAULT_COLORS = ["#FF5733", "#FF8D1A", "#FFC300", "#33FF57", "#33C1FF", "#3357FF", "#8E44AD", "#E91E63", "#009688", "#607D8B"];

export default function TaskList(props) {
  const { taskList, modifiedTaskDetails, setModifiedTaskDetails, onClickTaskDetail, userList, refreshTasks } = props;
  const [tasksState, setTasksState] = useState({});
  const [allTasksMap, setAllTasksMap] = useState({});
  const [showTaskForm, setShowTaskForm] = useState(null);
  const [showSubtaskForm, setShowSubtaskForm] = useState(null);
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [groupDetails, setGroupDetails] = useState({});

  // const [dates, setDates] = useState(null);

  const [collapsed, setCollapsed] = useState(() => {
    const collapsedParents = {};
    return collapsedParents;
  });

  const sensors = useSensors(useSensor(PointerSensor));

  // Build two things from incoming taskList:
  // 1) allTasksMap: id -> task (includes subtasks irrespective of status)
  // 2) tasksState: per-status arrays INCLUDING ONLY top-level tasks (parent === 0)
  useEffect(() => {
    if (!taskList?.statusList) return;

    const allMap = {};
    // First collect all tasks across all status buckets into allMap
    taskList.statusList.forEach((s) => {
      const key = s.task_status.replace(/\s+/g, "");
      const items = taskList[key] && taskList[key].data ? [...taskList[key].data] : [];
      const addTaskToMap = (task) => {
        allMap[String(task.id)] = { ...task };

        if (Array.isArray(task.sub_tasks)) {
          task.sub_tasks.forEach((sub) => {
            addTaskToMap(sub);
          });
        }
      };

      items.forEach(addTaskToMap);
    });

    // Then create per-status arrays containing only top-level tasks
    const initial = {};
    taskList.statusList.forEach((s) => {
      const key = s.task_status.replace(/\s+/g, "");
      const items = taskList[key] && taskList[key].data ? [...taskList[key].data] : [];
      initial[key] = items.filter((t) => t.parent == null || Number(t.parent) === 0);
    });

    setAllTasksMap(allMap);
    setTasksState(initial);
  }, [taskList]);

  const toggleCollapse = (taskId) => {
    setCollapsed((prev) => {
      const current = prev[taskId] ?? true; // default collapsed
      return {
        ...prev,
        [taskId]: !current,
      };
    });
  };

  const buildVisibleFromParents = (parents = []) => {
    const result = [];

    const addChildrenRecursive = (parentId, depth) => {
      const children = Object.values(allTasksMap).filter((t) => String(t.parent) === String(parentId));
      // If you want a stable order for children you may sort here (e.g. by some order field)
      children.forEach((child) => {
        result.push({ ...child, depth });
        const isCollapsed = collapsed[child.id] ?? true;
        if (!isCollapsed) {
          addChildrenRecursive(child.id, depth + 1);
        }
      });
    };

    parents.forEach((p) => {
      result.push({ ...p, depth: 0 });
      const isCollapsed = collapsed[p.id] ?? true;
      if (!isCollapsed) {
        addChildrenRecursive(p.id, 1);
      }
    });

    return result;
  };

  const onHandleEvent = (event, setFunction) => {
    const { name, value } = event.target;
    setFunction((previousState) => ({ ...previousState, [name]: value }));
  };

  const handleStatusUpdate = async (id, updatedFields, prevStatus) => {
    const jwtToken = Cookies.get("jwtToken");

    // helper to convert status label -> statusKey (remove spaces)
    const labelToKey = (label) => (label ? String(label).replace(/\s+/g, "") : "");

    setTasksState((prev) => {
      const newState = { ...prev };

      // build a quick id -> task mapping from current state + allTasksMap
      const idToTask = {};
      Object.keys(prev).forEach((k) => {
        (prev[k] || []).forEach((t) => {
          idToTask[String(t.id)] = { ...t };
        });
      });
      // include also subtasks from allTasksMap so we can find them
      Object.values(allTasksMap || {}).forEach((t) => {
        idToTask[String(t.id)] = idToTask[String(t.id)] || { ...t };
      });

      // determine current bucket key for this task if not provided
      let prevKey = prevStatus ? labelToKey(prevStatus) : null;
      if (!prevKey) {
        for (const k of Object.keys(prev)) {
          if (prev[k].some((t) => String(t.id) === String(id))) {
            prevKey = k;
            break;
          }
        }
      }

      // ensure all buckets are shallow-cloned
      Object.keys(newState).forEach((k) => {
        newState[k] = [...(prev[k] || [])];
      });

      // If only non-status fields changed: find task and update in-place
      if (!updatedFields.task_status) {
        if (prevKey && newState[prevKey]) {
          const idx = newState[prevKey].findIndex((t) => String(t.id) === String(id));
          if (idx !== -1) {
            newState[prevKey][idx] = { ...newState[prevKey][idx], ...updatedFields };
          }
        } else {
          // fallback search across all buckets
          for (const k of Object.keys(newState)) {
            const idx = newState[k].findIndex((t) => String(t.id) === String(id));
            if (idx !== -1) {
              newState[k][idx] = { ...newState[k][idx], ...updatedFields };
              break;
            }
          }
        }
        // Also update allTasksMap locally (so children reflect updates)
        setAllTasksMap((prevMap) => ({ ...prevMap, [String(id)]: { ...(prevMap[String(id)] || {}), ...updatedFields } }));
        return newState;
      }

      // If status change requested:
      // find the task's current object (if available)
      let currentTaskObj = idToTask[String(id)] || null;

      // If we couldn't find the task in the current snapshot, create a minimal object:
      if (!currentTaskObj) {
        currentTaskObj = { id, ...updatedFields };
      }

      // Determine whether this task is a subtask (parent !== 0) using the object we found
      const isSubtask = currentTaskObj.parent != null && Number(currentTaskObj.parent) !== 0;

      // If it's a subtask -> only update its task_status in-place and do NOT move buckets.
      if (isSubtask) {
        // Update any bucket that contains this parent (the subtask remains displayed only under its parent).
        // We don't move it across status buckets - parent determines bucket.
        // But update allTasksMap so UI shows its new status icon.
        setAllTasksMap((prevMap) => ({ ...prevMap, [String(id)]: { ...(prevMap[String(id)] || {}), ...updatedFields } }));
        return newState;
      }

      // Otherwise (task is parent/top-level) - move parent + descendants to new status bucket
      // const newKey = labelToKey(updatedFields.task_status);
      const statusObj = taskList.statusList.find((s) => s.id === updatedFields.task_status);

      const newKey = statusObj ? statusObj.task_status.replace(/\s+/g, "") : "";

      if (!newState[newKey]) newState[newKey] = [];

      // Build a map snapshot for descendant discovery (use allTasksMap for full tree)
      const snapshotMap = {};
      Object.values(allTasksMap || {}).forEach((t) => {
        snapshotMap[String(t.id)] = { ...t };
      });
      // Also include top-level items from prev state (they may not be in allTasksMap)
      Object.keys(prev).forEach((k) => {
        (prev[k] || []).forEach((t) => {
          snapshotMap[String(t.id)] = snapshotMap[String(t.id)] || { ...t };
        });
      });

      // BFS to find descendants of id
      const findDescendants = (rootId) => {
        const descendants = [];
        const queue = [String(rootId)];
        const seen = new Set(queue);
        while (queue.length) {
          const parentId = queue.shift();
          Object.values(snapshotMap).forEach((task) => {
            if (task.parent != null && String(task.parent) === String(parentId) && !seen.has(String(task.id))) {
              descendants.push({ ...task });
              seen.add(String(task.id));
              queue.push(String(task.id));
            }
          });
        }
        return descendants;
      };

      const allToMove = [String(id)];
      const descendants = findDescendants(id);
      descendants.forEach((d) => allToMove.push(String(d.id)));

      // Remove them from their current buckets
      Object.keys(newState).forEach((k) => {
        newState[k] = newState[k].filter((t) => !allToMove.includes(String(t.id)));
      });

      // Update local allTasksMap for parent + descendants so UI (icons/status) reflect new status.
      // But do NOT add descendants into tasksState — they'll render only via allTasksMap under their parent.
      setAllTasksMap((prevMap) => {
        const copy = { ...prevMap };
        // update parent
        copy[String(id)] = { ...(copy[String(id)] || {}), ...preparedParent };

        // update descendants metadata (but keep their own task_status)
        descendants.forEach((d) => {
          const did = String(d.id);
          const existing = copy[did] || {};
          // Merge any other changed fields, but do NOT overwrite their task_status.
          copy[did] = { ...existing, ...(d || {}) };
          // If you actually _want_ them to inherit parent's status, set task_status here.
        });

        return copy;
      });
      // Insert parent first
      // Insert parent only (do NOT put descendants into tasksState - keep tasksState as top-level tasks only)
      const preparedParent = { ...currentTaskObj, ...updatedFields, task_status: updatedFields.task_status };

      // Put only the parent into the destination bucket (preserve buckets as containing top-level tasks only)
      newState[newKey] = [preparedParent, ...newState[newKey]];

      return newState;
    });

    // Backend updates:
    try {
      if (updatedFields.task_status) {
        // If subtask, update only that task on backend.
        // Need to detect whether this id is a subtask from the current tasksState snapshot.
        const snapshotMap = {};
        Object.keys(tasksState || {}).forEach((k) => {
          (tasksState[k] || []).forEach((t) => {
            snapshotMap[String(t.id)] = t;
          });
        });
        const taskInSnapshot = snapshotMap[String(id)];
        const isSubtaskSnapshot = taskInSnapshot && taskInSnapshot.parent != null && Number(taskInSnapshot.parent) !== 0;

        const options = {
          method: "POST",
          headers: {
            Authorization: `Bearer ${jwtToken}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ task_status: updatedFields.task_status }),
        };

        // If subtask, update only the subtask
        if (isSubtaskSnapshot) {
          await fetch(CREATE_TASK_URL + id, options);
        } else {
          // parent update then update descendants (preserve previous behavior)
          await fetch(CREATE_TASK_URL + id, options);

          // find descendants from snapshotMap (we can use allTasksMap instead but follow existing logic)
          const findDescendantsBackend = (rootId) => {
            const result = [];
            const q = [String(rootId)];
            const seen = new Set(q);
            while (q.length) {
              const pid = q.shift();
              Object.values(snapshotMap).forEach((task) => {
                if (task.parent != null && String(task.parent) === String(pid) && !seen.has(String(task.id))) {
                  result.push(task);
                  seen.add(String(task.id));
                  q.push(String(task.id));
                }
              });
            }
            return result;
          };

          const descendantsForBackend = findDescendantsBackend(id);
          for (const desc of descendantsForBackend) {
            await fetch(CREATE_TASK_URL + desc.id, options);
          }
        }
      } else {
        // non-status field update -> single POST
        const options = {
          method: "POST",
          headers: {
            Authorization: `Bearer ${jwtToken}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(updatedFields),
        };

        const response = await fetch(CREATE_TASK_URL + id, options);
        if (!response.ok) {
          console.error("Failed to update task:", await response.text());
          Swal.fire({ title: "Error", text: "Failed to save changes.", icon: "error" });
        }
      }
    } catch (err) {
      console.error("Error updating task(s):", err);
      Swal.fire({ title: "Error", text: "Error saving changes.", icon: "error" });
    }
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        const jwtToken = Cookies.get("jwtToken");
        const options = {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${jwtToken}`,
            Accept: "application/json",
          },
        };

        try {
          const response = await fetch(DELETE_TASK_URL + id, options);
          if (response.ok) {
            // remove the task locally from tasksState so UI updates immediately
            setTasksState((prev) => {
              const updated = {};
              Object.keys(prev).forEach((k) => {
                updated[k] = prev[k].filter((t) => String(t.id) !== String(id));
              });
              return updated;
            });

            // remove from allTasksMap as well
            setAllTasksMap((prev) => {
              const copy = { ...prev };
              delete copy[String(id)];
              return copy;
            });

            refreshTasks();

            Swal.fire({
              title: "Deleted!",
              text: "Task has been deleted.",
              icon: "success",
            });
          } else {
            console.error("Failed to delete task");
            Swal.fire({
              title: "Error",
              text: "Failed to delete task.",
              icon: "error",
            });
          }
        } catch (error) {
          console.error("Error deleting task:", error);
          Swal.fire({
            title: "Error",
            text: "Error deleting task.",
            icon: "error",
          });
        }
      }
    });
  };

  const handleTaskSubmit = async (e, statusId) => {
    e.preventDefault();

    const formData = new FormData();

    for (const key in modifiedTaskDetails) {
      formData.append(key, modifiedTaskDetails[key]);
    }
    formData.append("parent", 0);
    formData.append("assignee", 0);
    formData.append("task_status", statusId);

    try {
      const jwtToken = Cookies.get("jwtToken");
      const options = {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${jwtToken}`,
          Accept: "application/json",
        },
      };

      const response = await fetch(CREATE_TASK_URL + "0", options);
      const data = await response.json();

      if (data.status === "Success") {
        setShowTaskForm(null);
        setModifiedTaskDetails({});
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleSubtaskSubmit = async (e, parentTask) => {
    e.preventDefault();

    const formData = new FormData();
    Object.keys(modifiedTaskDetails).forEach((key) => formData.append(key, modifiedTaskDetails[key]));

    formData.append("parent", parentTask.id);
    formData.append("assignee", 0);
    formData.append("task_status", parentTask.task_status);

    try {
      const jwtToken = Cookies.get("jwtToken");

      const response = await fetch(CREATE_TASK_URL + 0, {
        method: "POST",
        body: formData,
        headers: { Authorization: `Bearer ${jwtToken}` },
      });

      const data = await response.json();

      if (data.status === "Success") {
        setShowSubtaskForm(null);
        setModifiedTaskDetails({});
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleGroupSubmit = async (e) => {
    e.preventDefault();

    const formData = new FormData();

    // for (const key in groupDetails) {
    //   if (key === "color") {
    //     formData.append("color", `#${groupDetails.color}`);
    //   } else {
    //     formData.append(key, groupDetails[key]);
    //   }
    // }
    for (const key in groupDetails) {
      formData.append(key, groupDetails[key]);
    }

    try {
      const jwtToken = Cookies.get("jwtToken");
      const options = {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${jwtToken}`,
          Accept: "application/json",
        },
      };

      const response = await fetch(ADD_GROUP_URL, options);
      const data = await response.json();

      // 🔴 VALIDATION / DUPLICATE ERROR
      if (data.success === false) {
        Swal.fire({
          icon: "error",
          title: "Validation Error",
          text: data.message || "Something went wrong",
        });
        return;
      }

      if (data.status === "success") {
        Swal.fire({
          title: "Success",
          text: "Group added successfully.",
          icon: "success",
        });

        setShowGroupForm(false);
        setGroupDetails({});
        await refreshTasks();
      }
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Failed to add group", "error");
    }
  };

  const getStatusClass = (status) => {
    return status.toLowerCase().replace(/\s+/g, "-");
  };

  return (
    <>
      {taskList?.statusList ? (
        <div className="task-accordion">
          <Accordion multiple activeIndex={[0]}>
            {taskList.statusList.map((status, index) => {
              const { id, task_status, count, color } = status;
              const statusKey = task_status.replace(/\s+/g, "");
              // tasksState[statusKey] now contains only top-level tasks (parent === 0)
              const tasksData = tasksState[statusKey] || [];

              // build visible tasks from the local tasksData (parents) but fetch subtasks from allTasksMap
              const visibleTasks = buildVisibleFromParents(tasksData);

              // per-status drag end handler that updates tasksState for this statusKey
              const handleDragEnd = ({ active, over, delta }) => {
                if (!over || String(active.id) === String(over.id)) return;

                // find indexes inside the visibleTasks array
                const activeIndex = visibleTasks.findIndex((t) => String(t.id) === String(active.id));
                const overIndex = visibleTasks.findIndex((t) => String(t.id) === String(over.id));

                // if either index is -1, bail (safety)
                if (activeIndex === -1 || overIndex === -1) return;

                // For simplicity we still reorder the top-level tasksData array by mapping through visibleTasks
                // but ensure we only move top-level entries (parents). If user drags a subtask you'll need to
                // decide desired behavior — here we allow moving within the flattened view, which may affect parent relationships.
                // If you want to restrict dragging to parents only, change SortableContext items to parents only and
                // change active/over handling accordingly.

                // Create a flat copy of visibleTasks, move items:
                const newVisible = arrayMove([...visibleTasks], activeIndex, overIndex);

                // Now reconstruct a new tasksData array by:
                // put visible parents in the new order (filter only parents), then append remaining (e.g. collapsed children)
                const idToTask = new Map(tasksData.map((t) => [String(t.id), { ...t }]));
                const reorderedVisibleIds = newVisible.map((t) => String(t.id));

                // Keep only parents from reorderedVisibleIds
                const reorderedParentIdsInVisible = newVisible.filter((t) => t.parent == null || Number(t.parent) === 0).map((t) => String(t.id));

                const remaining = tasksData.filter((t) => !reorderedParentIdsInVisible.includes(String(t.id)));
                const reordered = [...reorderedParentIdsInVisible.map((id) => idToTask.get(String(id))), ...remaining];

                // handle indenting via delta.x (optional - mimic original intent)
                const indentThreshold = 25;
                const movedIndexInReordered = reordered.findIndex((t) => String(t.id) === String(active.id));
                if (movedIndexInReordered !== -1) {
                  const movedTask = { ...reordered[movedIndexInReordered] };
                  if (delta && delta.x > indentThreshold) {
                    // make the moved task child of the previous item if possible
                    const above = reordered[movedIndexInReordered - 1];
                    if (above && String(above.id) !== String(movedTask.id)) {
                      movedTask.parent = above.id;
                      // update global map so children rendering follows new parent
                      setAllTasksMap((prev) => ({ ...prev, [String(movedTask.id)]: { ...(prev[String(movedTask.id)] || {}), parent: above.id } }));
                    }
                  } else if (delta && delta.x < -indentThreshold) {
                    // outdent -> make parent null
                    movedTask.parent = 0;
                    setAllTasksMap((prev) => ({ ...prev, [String(movedTask.id)]: { ...(prev[String(movedTask.id)] || {}), parent: 0 } }));
                  }
                  reordered[movedIndexInReordered] = movedTask;
                }

                // Update tasksState for this status
                setTasksState((prev) => ({ ...prev, [statusKey]: reordered }));
              };

              return (
                <AccordionTab
                  key={task_status}
                  header={
                    <>
                      <span
                        className="task-status-title"
                        style={{
                          backgroundColor: color || "#dadada",
                          color: getTextColor(color),
                        }}
                      >
                        {task_status}
                      </span>
                      <span className="font-bold white-space-nowrap">{count}</span>
                    </>
                  }
                  className={`box-gray ${getStatusClass(task_status)}`}
                >
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    {/* NOTE: SortableContext currently uses visibleTasks (parents + children).
                        If you want drag to operate only on parents, change items to tasksData.map(t => String(t.id)) */}
                    <SortableContext items={visibleTasks.map((t) => String(t.id))} strategy={verticalListSortingStrategy}>
                      <table className="table draggable-table">
                        <thead>
                          <tr>
                            <th>
                              <div className="form-check">
                                <label className="form-check-label"></label>Name
                              </div>
                            </th>
                            <th>Assignee</th>
                            <th>Due date</th>
                            <th>Priority</th>
                            <th>
                              <i className="ri-add-circle-line"></i>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {visibleTasks.map((task) => {
                            const isParent = Object.values(allTasksMap).some((t) => String(t.parent) === String(task.id));

                            const subtaskCount = Object.values(allTasksMap).filter((t) => String(t.parent) === String(task.id)).length;

                            return (
                              <React.Fragment key={String(task.id)}>
                                <Task
                                  key={task.id}
                                  task={task}
                                  depth={task.depth}
                                  isParent={isParent}
                                  isCollapsed={collapsed[task.id] ?? true}
                                  handleDelete={handleDelete}
                                  onToggle={toggleCollapse}
                                  subtaskCount={subtaskCount}
                                  onClickTaskDetail={onClickTaskDetail}
                                  task_status={task.task_status}
                                  onUpdate={(updatedFields) => handleStatusUpdate(task.id, updatedFields, statusKey)}
                                  statusOptions={taskList.statusList}
                                  onAddSubtask={() => setShowSubtaskForm(task.id)}
                                  userList={userList}
                                />
                                {showSubtaskForm === task.id && (
                                  <tr key={`subtask-form-${task.id}`}>
                                    <td colSpan={5} style={{ paddingLeft: `${(task.depth + 1) * 16}px` }}>
                                      <form className="task-add-form subtask-add-form" onSubmit={(e) => handleSubtaskSubmit(e, task)}>
                                        {task.task_status === "completed" ? (
                                          <i className="fa fa-check-circle"></i>
                                        ) : task.task_status === "in progress" ? (
                                          <i className="fa fa-adjust"></i>
                                        ) : (
                                          <i className="ri-donut-chart-fill"></i>
                                        )}
                                        <div className="form-group">
                                          <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Enter subtask name"
                                            name="title"
                                            autoFocus
                                            value={modifiedTaskDetails.title || ""}
                                            onChange={(e) => onHandleEvent(e, setModifiedTaskDetails)}
                                          />
                                        </div>

                                        <div className="btn-stack">
                                          <Calendar
                                            className="task-table-calendar"
                                            value={modifiedTaskDetails.dueDate || ""}
                                            name="dueDate"
                                            onChange={(e) =>
                                              setModifiedTaskDetails((prev) => ({
                                                ...prev,
                                                dueDate: e.value,
                                              }))
                                            }
                                            showButtonBar
                                            readOnlyInput
                                            dateFormat="M dd yy"
                                            showIcon
                                          />
                                        </div>

                                        <div className="submit-btns">
                                          <button
                                            type="button"
                                            className="btn btn-exit"
                                            onClick={() => {
                                              setShowSubtaskForm(null);
                                              setModifiedTaskDetails({});
                                            }}
                                          >
                                            Cancel
                                          </button>

                                          <button type="submit" className="btn btn-save" disabled={!modifiedTaskDetails.title}>
                                            Save
                                          </button>
                                        </div>
                                      </form>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                          {showTaskForm === task_status && (
                            <tr>
                              <td colSpan={5}>
                                <form className="task-add-form" onSubmit={(e) => handleTaskSubmit(e, status.id)}>
                                  {task_status === "completed" ? (
                                    <i className="fa fa-check-circle"></i>
                                  ) : task_status === "in progress" ? (
                                    <i className="fa fa-adjust"></i>
                                  ) : (
                                    <i className="ri-donut-chart-fill"></i>
                                  )}
                                  <div className="form-group">
                                    <input
                                      type="text"
                                      className="form-control"
                                      placeholder="Enter task name"
                                      name="title"
                                      autoFocus
                                      value={modifiedTaskDetails.title || ""}
                                      onChange={(e) => {
                                        onHandleEvent(e, setModifiedTaskDetails);
                                      }}
                                    />
                                  </div>
                                  <div className="btn-stack">
                                    <Calendar
                                      className="task-table-calendar"
                                      value={modifiedTaskDetails.dueDate || ""}
                                      name="dueDate"
                                      onChange={(e) => {
                                        setModifiedTaskDetails((prev) => ({
                                          ...prev,
                                          dueDate: e.value,
                                        }));
                                      }}
                                      readOnlyInput
                                      dateFormat="M dd yy"
                                      showButtonBar
                                      showIcon
                                    />
                                    <i className="ri-user-add-line"></i>
                                    <i className="ri-flag-2-line"></i>
                                  </div>
                                  <div>
                                    <button
                                      type="button"
                                      className="btn btn-exit"
                                      onClick={() => {
                                        setShowTaskForm(null);
                                        setModifiedTaskDetails({});
                                      }}
                                    >
                                      Cancel
                                    </button>
                                    <button type="submit" className="btn btn-save" disabled={!modifiedTaskDetails.title || modifiedTaskDetails.title.trim() === ""}>
                                      Save
                                    </button>
                                  </div>
                                </form>
                              </td>
                            </tr>
                          )}
                          {showTaskForm !== task_status && (
                            <tr>
                              <td>
                                <button
                                  type="button"
                                  className="btn add-btn"
                                  onClick={() => {
                                    setShowTaskForm(task_status);
                                  }}
                                >
                                  <i className="ri-add-line"></i> Add task
                                </button>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </SortableContext>
                  </DndContext>
                </AccordionTab>
              );
            })}
          </Accordion>

          <div className="add-board-group mt-3">
            <button className="btn add-group-btn" onClick={() => setShowGroupForm((prev) => !prev)}>
              <i className="ri-add-line"></i> Add group
            </button>
            {showGroupForm && (
              <div className="task-dropdown">
                <form className="dropdown-item" onSubmit={handleGroupSubmit}>
                  <input type="text" className="form-control" name="title" placeholder="STATUS NAME" value={groupDetails.title || ""} onChange={(e) => onHandleEvent(e, setGroupDetails)} />
                  <div className="group-color-grid">
                    <div className="color-picker-list">
                      {DEFAULT_COLORS.map((color) => (
                        <span
                          key={color}
                          className={`color-circle ${groupDetails.color === color ? "active" : ""}`}
                          style={{ backgroundColor: color }}
                          onClick={() =>
                            setGroupDetails((prev) => ({
                              ...prev,
                              color: color,
                            }))
                          }
                        />
                      ))}
                    </div>
                    <div className="btn-stack d-flex">
                      <button type="submit" className="btn-save" disabled={!groupDetails.title?.trim()}>
                        <i className="ri-check-line"></i>
                      </button>

                      <button
                        type="button"
                        className="btn-exit"
                        onClick={() => {
                          setShowGroupForm(false);
                          setGroupDetails({});
                        }}
                      >
                        <i className="ri-close-line"></i>
                      </button>
                    </div>
                  </div>
                  {/* <ColorPicker
                    value={groupDetails.color || ""}
                    onChange={(e) =>
                      setGroupDetails((prev) => ({
                        ...prev,
                        color: e.value,
                      }))
                    }
                  /> */}
                </form>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="loading-hover">
          <div className="property-loader preloader">
            <div className="loading">
              <div className="loading__letter">L</div>
              <div className="loading__letter">o</div>
              <div className="loading__letter">a</div>
              <div className="loading__letter">d</div>
              <div className="loading__letter">i</div>
              <div className="loading__letter">n</div>
              <div className="loading__letter">g</div>
              <div className="loading__letter">.</div>
              <div className="loading__letter">.</div>
              <div className="loading__letter">.</div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
